import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatShort = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
};

// Backend expects dd/MM/yyyy — convert from our internal yyyy-MM-dd keys
const toDDMMYYYY = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const getSlotKey = (slot, index) =>
  typeof slot === "string"
    ? slot
    : (slot?.id ??
      slot?.slotId ??
      `${slot?.startTime ?? ""}-${slot?.endTime ?? ""}` ??
      index);

const getSlotLabel = (slot) => {
  if (typeof slot === "string") return slot;
  if (slot?.startTime && slot?.endTime)
    return `${slot.startTime} – ${slot.endTime}`;
  return slot?.label ?? slot?.time ?? slot?.slot ?? JSON.stringify(slot);
};

function DayCard({ date, isSelected, isToday, isPast, onClick }) {
  return (
    <Box
      onClick={isPast ? undefined : onClick}
      sx={{
        width: 46,
        height: 60,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: isPast ? "not-allowed" : "pointer",
        opacity: isPast ? 0.4 : 1,
        border: `1.5px solid ${isSelected ? ACCENT : "#e0e0e0"}`,
        background: isSelected ? ACCENT : isToday ? "#eef3fc" : "#fff",
        color: isSelected ? "#fff" : "#333",
        transition: "all 0.15s",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {DAY_ABBR[date.getDay()]}
      </Typography>
      <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
        {date.getDate()}
      </Typography>
    </Box>
  );
}

function SlotChip({ label, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.4,
        py: 0.7,
        borderRadius: "9px",
        cursor: "pointer",
        fontSize: "0.78rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        border: `1.5px solid ${selected ? ACCENT : "#e0e0e0"}`,
        background: selected ? HEADER_GRADIENT : "#fff",
        color: selected ? "#fff" : "#455a64",
        boxShadow: selected ? "0 3px 10px rgba(23,71,158,0.35)" : "none",
        transform: selected ? "scale(1.04)" : "scale(1)",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: ACCENT,
          background: selected ? HEADER_GRADIENT : "#eef3fc",
        },
      }}
    >
      {selected ? (
        <CheckCircleIcon sx={{ fontSize: 15 }} />
      ) : (
        <AccessTimeIcon sx={{ fontSize: 15, color: "#9aa4b2" }} />
      )}
      {label}
    </Box>
  );
}

function MentorAvailabilityDialog({ open, mentorRow, onClose, onSuccess }) {
  const today = useMemo(() => startOfToday(), []);

  const [viewMode, setViewMode] = useState("weekly");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthAnchor, setMonthAnchor] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [selectedDates, setSelectedDates] = useState(new Set());

  const [sessionTypes, setSessionTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [sessionTypeIds, setSessionTypeIds] = useState([]);

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlotKeys, setSelectedSlotKeys] = useState(new Set());

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setViewMode("weekly");
    setWeekOffset(0);
    setMonthAnchor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDates(new Set());
    setSessionTypeIds([]);
    setSlots([]);
    setSelectedSlotKeys(new Set());
    setLoadingTypes(true);
    api
      .get("/admin/mentor-session-types")
      .then((res) => {
        const raw = res.data?.data;
        setSessionTypes(Array.isArray(raw) ? raw : (raw?.content ?? []));
      })
      .catch((err) => {
        console.error("Session types fetch error:", err);
        toast.error("Failed to load session types.");
      })
      .finally(() => setLoadingTypes(false));
  }, [open, today]);

  const weekDays = useMemo(() => {
    const start = addDays(today, weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [today, weekOffset]);

  const monthGrid = useMemo(() => {
    const year = monthAnchor.getFullYear();
    const month = monthAnchor.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = firstDay.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1)
      cells.push(new Date(year, month, day));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthAnchor]);

  const weekLabel = `${weekDays[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;
  const monthLabel = `${MONTH_NAMES[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`;
  const isCurrentMonth =
    monthAnchor.getFullYear() === today.getFullYear() &&
    monthAnchor.getMonth() === today.getMonth();

  const toggleDate = (date) => {
    const zeroed = new Date(date);
    zeroed.setHours(0, 0, 0, 0);
    if (zeroed < today) return;
    const iso = toISODate(date);
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });
  };

  const removeDate = (iso) =>
    setSelectedDates((prev) => {
      const next = new Set(prev);
      next.delete(iso);
      return next;
    });

  const sortedSelectedDates = Array.from(selectedDates).sort();

  useEffect(() => {
    if (!open) return;
    if (!sessionTypeIds.length) {
      setSlots([]);
      setSelectedSlotKeys(new Set());
      return;
    }
    setLoadingSlots(true);
    api
      .get("/mentor-catalog/timeslots", {
        params: { sessionTypeId: sessionTypeIds },
        // Send repeated "sessionTypeId=2&sessionTypeId=20" instead of
        // axios' default "sessionTypeId[]=2&sessionTypeId[]=20"
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
              params[key].forEach((value) => searchParams.append(key, value));
            } else {
              searchParams.append(key, params[key]);
            }
          });
          return searchParams.toString();
        },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        console.log("Generated timeslots:", data);
        setSlots(Array.isArray(data) ? data : []);
        setSelectedSlotKeys(new Set());
      })
      .catch((err) => {
        console.error("Timeslots fetch error:", err);
        toast.error(
          err.response?.data?.errorMessage ||
            "Failed to load time slots for the selected session types.",
        );
        setSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [open, sessionTypeIds]);

  const toggleSlot = (key) =>
    setSelectedSlotKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleSubmit = async () => {
    if (!mentorRow) return;
    setSaving(true);
    try {
      const selectedTimeSlots = slots
        .filter((slot, i) => selectedSlotKeys.has(getSlotKey(slot, i)))
        .map((slot) => getSlotLabel(slot));

      await api.post(
        `/admin/mentors/${mentorRow.mentorId}/availability/slots`,
        {
          availability: {
            slots: [
              {
                slotType: viewMode === "monthly" ? "Monthly" : "Weekly",
                sessionTypes: sessionTypeIds,
                dates: sortedSelectedDates.map(toDDMMYYYY),
                timeSlots: selectedTimeSlots,
              },
            ],
          },
        },
      );
      toast.success("Availability updated successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to update availability. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventAvailableIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}
            >
              Set Availability
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.78rem" }}
            >
              {mentorRow?.mentorName ?? "—"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, v) => setViewMode(v)}
          sx={{
            mb: 2,
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Weekly" value="weekly" />
          <Tab label="Monthly" value="monthly" />
        </Tabs>

        {viewMode === "weekly" ? (
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.2,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
                disabled={weekOffset <= 0}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#37474f" }}
              >
                {weekLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setWeekOffset((o) => o + 1)}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.8,
                justifyContent: "center",
              }}
            >
              {weekDays.map((d) => {
                const iso = toISODate(d);
                return (
                  <DayCard
                    key={iso}
                    date={d}
                    isSelected={selectedDates.has(iso)}
                    isToday={toISODate(today) === iso}
                    isPast={d < today}
                    onClick={() => toggleDate(d)}
                  />
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.2,
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  setMonthAnchor((d) => {
                    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                    const curStart = new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      1,
                    );
                    return prev < curStart ? d : prev;
                  })
                }
                disabled={isCurrentMonth}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#37474f" }}
              >
                {monthLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setMonthAnchor(
                    (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1),
                  )
                }
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.6,
                mb: 0.6,
              }}
            >
              {DAY_ABBR.map((label) => (
                <Typography
                  key={label}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#9aa4b2",
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.6,
              }}
            >
              {monthGrid.map((d, i) => {
                if (!d) return <Box key={`blank-${i}`} />;
                const iso = toISODate(d);
                return (
                  <Box
                    key={iso}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <DayCard
                      date={d}
                      isSelected={selectedDates.has(iso)}
                      isToday={toISODate(today) === iso}
                      isPast={d < today}
                      onClick={() => toggleDate(d)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {sortedSelectedDates.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#8a94a6",
                fontWeight: 600,
                mb: 0.8,
              }}
            >
              SELECTED DATES ({sortedSelectedDates.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
              {sortedSelectedDates.map((iso) => (
                <Chip
                  key={iso}
                  label={formatShort(iso)}
                  size="small"
                  onDelete={() => removeDate(iso)}
                  sx={{ bgcolor: "#eef3fc", color: ACCENT, fontWeight: 600 }}
                />
              ))}
            </Box>
          </Box>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Session Types</InputLabel>
          {loadingTypes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Select
              multiple
              label="Session Types"
              value={sessionTypeIds}
              onChange={(e) => setSessionTypeIds(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const st = sessionTypes.find((s) => s.id === id);
                    return (
                      <Chip key={id} label={st?.name ?? id} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {sessionTypes.map((st) => (
                <MenuItem key={st.id} value={st.id}>
                  <Checkbox
                    checked={sessionTypeIds.includes(st.id)}
                    size="small"
                  />
                  <ListItemText primary={st.name} />
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        {sessionTypeIds.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#8a94a6",
                fontWeight: 600,
                mb: 0.8,
              }}
            >
              AVAILABLE TIME SLOTS
            </Typography>
            {loadingSlots ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={20} />
              </Box>
            ) : slots.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No time slots available for the selected session type(s).
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                {slots.map((slot, i) => {
                  const key = getSlotKey(slot, i);
                  const selected = selectedSlotKeys.has(key);
                  return (
                    <SlotChip
                      key={key}
                      label={getSlotLabel(slot)}
                      selected={selected}
                      onClick={() => toggleSlot(key)}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSubmit}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Saving..." : "Save Availability"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorAvailabilityDialog;
